import type { ConversationSearchResult, SafeWhatsAppSession } from "../api/client.js";
import { useMemo, useState } from "react";

export function ConversationList(props: {
  conversations: readonly ConversationSearchResult[];
  onSelect: (conversation: ConversationSearchResult) => void;
  selectedCustomerId: string | null;
  session?: SafeWhatsAppSession | null;
  loading?: boolean;
}): React.JSX.Element {
  const [query, setQuery] = useState("");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const conversations = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase();
    return props.conversations.filter((item) => (!unreadOnly || (item.unreadCount ?? 0) > 0) && (normalized.length === 0 || [
      item.customer.name,
      item.customer.phoneNumber,
      item.lastMessagePreview ?? "",
      item.latestIntent ?? ""
    ].some((value) => value.toLocaleLowerCase().includes(normalized))));
  }, [props.conversations, query, unreadOnly]);

  return (
    <section aria-label="会话列表" className="wa-conversation-list">
      <header>
        <div aria-hidden="true" className="wa-avatar wa-avatar--self">S</div>
        <div className="wa-conversation-list__heading">
          <h2>客户会话</h2>
          <small>SCS · {props.session?.accountPhoneE164 ?? "号码待解析"} · {props.session?.status === "CONNECTED" && props.session.sendAllowed ? "已连接" : "未连接"}</small>
        </div>
      </header>
      <label className="wa-conversation-list__search">
        <span className="sr-only">搜索会话</span>
        <svg aria-hidden="true" className="wa-conversation-list__search-icon" fill="none" viewBox="0 0 24 24">
          <circle cx="10.8" cy="10.8" r="6.8" stroke="currentColor" strokeWidth="2" />
          <path d="m16 16 4.2 4.2" stroke="currentColor" strokeLinecap="round" strokeWidth="2" />
        </svg>
        <input
          onChange={(event) => { setQuery(event.currentTarget.value); }}
          placeholder="搜索联系人或消息"
          type="search"
          value={query}
        />
      </label>
      <button aria-pressed={unreadOnly} className="wa-unread-filter" onClick={() => { setUnreadOnly((value) => !value); }} type="button">未读 {props.conversations.reduce((count, item) => count + (item.unreadCount ?? 0), 0)}</button>
      {props.loading ? <div aria-live="polite" className="wa-list-state" role="status"><span aria-hidden="true" className="wa-list-state__icon">…</span><strong>正在加载会话</strong><small>请稍候，正在同步最新消息。</small></div>
        : conversations.length === 0 ? <div className="wa-list-state"><span aria-hidden="true" className="wa-list-state__icon">⌕</span><strong>{props.conversations.length === 0 ? "暂无会话" : "没有匹配的会话"}</strong><small>{props.conversations.length === 0 ? "收到新消息后，会话会显示在这里。" : "试试其他关键词或关闭未读筛选。"}</small></div> : null}
      <ul>
        {conversations.map((item) => (
          <li key={item.customer.id}>
            <button
              aria-current={props.selectedCustomerId === item.customer.id ? "true" : undefined}
              disabled={item.conversationId === null}
              onClick={() => { props.onSelect(item); }}
              type="button"
            >
              <span aria-hidden="true" className="wa-avatar wa-avatar--customer">{avatarLabel(item.customer.phoneNumber, item.customer.name)}</span>
              <span className="wa-conversation-list__copy">
                <strong title={conversationLabel(item.customer.name, item.customer.phoneNumber)}>{conversationLabel(item.customer.name, item.customer.phoneNumber)}</strong>
                <small>{item.lastMessagePreview ?? item.latestIntent ?? "暂无消息"}</small>
              </span>
              <span className="wa-conversation-list__meta"><time>{item.lastMessageAt === null ? "" : new Date(item.lastMessageAt).toLocaleDateString()}</time>{(item.unreadCount ?? 0) > 0 ? <span className="wa-unread-badge" aria-label={`${item.unreadCount} 条未读`}>{item.unreadCount}</span> : null}</span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function conversationLabel(name: string, phoneNumber: string): string {
  if (phoneNumber.startsWith("wa-lid:")) return "WhatsApp 联系人（号码待解析）";
  const normalizedName = name.trim();
  // WhatsApp itself shows a number as the primary label for people who are not
  // saved contacts.  The importer previously generated "WhatsApp <number>";
  // never expose that synthetic name in the workbench.
  if (normalizedName === "" || /^whatsapp(?:\s|$)/iu.test(normalizedName)) return phoneNumber;
  return normalizedName;
}

function avatarLabel(phoneNumber: string, name: string): string {
  if (phoneNumber.startsWith("wa-lid:")) return "?";
  const digits = phoneNumber.replace(/\D/gu, "");
  if (digits.length >= 2) return digits.slice(-2);
  return name.trim().slice(0, 1).toLocaleUpperCase() || "?";
}
